import { useEffect, useState, ReactNode, Suspense } from 'react'

import classnames from 'classnames'
import { ErrorBoundary } from 'react-error-boundary'
import { Route, Routes, useLocation, RouteProps } from 'react-router-dom'
import { animated, useTransition } from 'react-spring'
import styled from 'styled-components'

import { gaSendPageview } from 'analytics'
import { useAudioContext } from 'audioContext'
import { ErrorFallback } from 'components/ErrorFallback'
import { Navigation } from 'components/Navigation'
import { NavigationVars } from 'components/Navigation/styles'
import { AppHeaderVars } from 'layouts/Layout/styles'
import { MAX_WIDTH } from 'styles/theme'

const View = styled.main`
  --player-height-offset: 0px;
  position: absolute;
  ${NavigationVars}
  ${AppHeaderVars}
  width: 100%;

  & > div:not(:first-child) {
    left: 0;
    margin: 0 auto;
    max-width: ${MAX_WIDTH};
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
  }

  &.audioPlayerPadding {
    --player-height-offset: 60px;
  }

  @media only screen and (min-width: 650px) {
    &.audioPlayerPadding {
      --player-height-offset: 130px;
    }
  }
`

const isChild = (path: string) =>
  (path.replace(/\/$/g, '').match(/\//g) || []).length > 1

const isHome = (path: string) => path === '/'
const wasChild = (path: string, prevPath: string) => prevPath.includes(path)

const fromTransition = (
  path: string,
  initial: boolean,
  previousPath: string
) => {
  const wasPathChild = wasChild(path, previousPath)
  const isHomePath = isHome(path)
  const isChildPath = isChild(path)
  let transform = 'translate3d(0%,0,0)'
  if (!initial && (wasPathChild || isHomePath))
    transform = 'translate3d(-20%,0,0)'
  if (!initial && isChildPath) transform = 'translate3d(100%,0, 0)'
  return {
    transform,
    opacity: initial || isHomePath || wasPathChild || isChildPath ? 1 : 0,
  }
}

const leaveTransition = (path: string, previousPath: string) => {
  const wasPathChild = wasChild(path, previousPath)
  const isHomePath = isHome(path)
  const isChildPath = isChild(path)
  let transform = 'translate3d(0%,0,0)'
  if (isHomePath || wasPathChild) transform = 'translate3d(100%,0, 0)'
  if (isChildPath && !wasPathChild) transform = 'translate3d(-20%,0, 0)'
  return {
    zIndex: isChildPath ? 0 : 2,
    transform,
    opacity: isHomePath || isChildPath || wasPathChild ? 1 : 0,
  }
}

const RoutesComponent = ({
  routes,
  children,
}: {
  children: ReactNode
  routes: Array<
    RouteProps & {
      i18nKey?: string
      nested?: RouteProps
    }
  >
}) => {
  const location = useLocation()
  const { showPlayer } = useAudioContext()
  const [previousPath, setPreviousPath] = useState<string>('')
  const [initialRoute, setInitialRoute] = useState<boolean>(true)

  const [largeScreen, setLargeScreen] = useState<boolean>(false)

  const getWindowSize = () => {
    const bool = window.innerWidth > 960
    setLargeScreen(bool)
  }

  useEffect(() => {
    getWindowSize()
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    setPreviousPath(location.pathname)
    // Analytics for new location
    if (location.pathname) gaSendPageview(location.pathname)
  }, [location])

  useEffect(() => {
    setInitialRoute(false)
  }, [])

  // Used to ensure animation overflows are hidden.
  // This prevents having to keep an overflow when not animating views which
  // will break things like sticky headers.
  const [animating, setAnimating] = useState(false)
  useEffect(() => {
    if (animating) {
      document.body.classList.add('animation--overflow--hidden')
    } else {
      document.body.classList.remove('animation--overflow--hidden')
    }
  }, [animating])

  const noTransition = (curr: string, prev: string) => {
    if (curr.split('?')[0] === prev.split('?')[0]) return true
    return false
  }

  const transitions = useTransition(location, {
    from: !noTransition(location.pathname, previousPath)
      ? fromTransition(location.pathname, initialRoute, previousPath)
      : { opacity: 1 },
    enter: !noTransition(location.pathname, previousPath)
      ? {
          opacity: 1,
          transform: 'translate3d(0%,0,0)',
        }
      : { opacity: 1 },
    leave: !noTransition(location.pathname, previousPath)
      ? leaveTransition(location.pathname, previousPath)
      : { opacity: 1 },
    config: {
      duration: 380,
    },
    onStart: () => {
      document.body.classList.add('animation--overflow--hidden')
      setAnimating(true)
    },
    onRest() {
      setAnimating(false)
      setPreviousPath(location.pathname)
    },
  })

  return (
    <View
      className={classnames({
        audioPlayerPadding: showPlayer,
      })}
    >
      <Navigation routes={routes} />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<div className="pageLoad" />}>
          {largeScreen ? (
            <Routes>
              {routes.map(({ path, element: Element }) => (
                <Route
                  key={Array.isArray(path) ? path[0] : path}
                  path={path}
                  element={Element}
                />
              ))}
            </Routes>
          ) : (
            transitions((props, item) => (
              <animated.div style={props} key={item.key}>
                <Routes location={item}>
                  {children}
                  {routes.map(
                    ({ path, element: Element, nested: nestedRoute }) => (
                      <Route
                        key={Array.isArray(path) ? path[0] : path}
                        path={path}
                        element={Element}
                      >
                        {nestedRoute && (
                          <Route
                            key={
                              Array.isArray(nestedRoute.path)
                                ? nestedRoute.path[0]
                                : nestedRoute.path
                            }
                            path={nestedRoute.path}
                            element={nestedRoute.element}
                          />
                        )}
                      </Route>
                    )
                  )}
                </Routes>
              </animated.div>
            ))
          )}
        </Suspense>
      </ErrorBoundary>
    </View>
  )
}

export default RoutesComponent
